import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

/**
 * Auth provider — backed by httpOnly cookies (not localStorage).
 *
 * The browser stores the access_token in an httpOnly cookie that JavaScript
 * cannot read (defeats XSS-based token theft). We keep ONLY the user profile
 * in React state, and on every page refresh we ask the backend "who am I?"
 * via /api/auth/me. The cookie is sent automatically because axios is
 * configured with withCredentials=true.
 */
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const response = await authAPI.getMe();
            setUser(response.data);
        } catch {
            // 401 (no/expired cookie) is the normal "logged out" state — silent
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        // Backend sets the access_token cookie on the response — we just need
        // to capture the user object it returns and stash it in React state.
        const response = await authAPI.login({ email, password });
        const userData = response.data.user;
        setUser(userData);
        return userData;
    };

    const register = async (email, password, fullName, phone, referralCode) => {
        const response = await authAPI.register({
            email,
            password,
            full_name: fullName,
            phone,
            referral_code: referralCode || null,
        });
        const userData = response.data.user;
        setUser(userData);
        return userData;
    };

    const logout = async () => {
        // Always clear local state first so the UI updates immediately even
        // if the network call fails or is slow.
        setUser(null);
        try {
            await authAPI.logout();
        } catch {
            // Best-effort — if the call fails the cookie may linger until expiry,
            // but the user is already logged out from the app's perspective.
        }
    };

    const updateUser = (updates) => {
        setUser((prev) => (prev ? { ...prev, ...updates } : prev));
    };

    const refreshUser = async () => {
        try {
            const response = await authAPI.getMe();
            setUser(response.data);
        } catch {
            // Silent — the 401 interceptor in api.js will redirect to /login
            // if the user's session has truly expired.
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            register,
            logout,
            updateUser,
            refreshUser,
            isAuthenticated: !!user,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
